import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, CheckSquare, Search, AlertCircle, BookOpen, Shield, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TAABIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/ed75ipfo_Ta%C3%A0biy.png';
const YEEBIY = 'https://customer-assets.emergentagent.com/job_lamnso-hub/artifacts/49uszg6d_Ye%C3%A8biy.png';

const STORAGE_KEY = 'lamfon_beta_onboarding_seen';

/**
 * Elder Phase 1 Beta Onboarding Modal.
 * Shows once on first login for Elder-level users.
 * Can be dismissed and revisited from the info section.
 */
export function BetaOnboardingModal({ user, onClose }) {
  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="beta-onboarding-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#FAFAF9] w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full bg-white/80 text-[#A8A29E] hover:text-[#1C1917] z-10" data-testid="close-onboarding">
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-[#4CAF50] to-[#3D8B40] px-6 pt-8 pb-12 text-center rounded-t-3xl sm:rounded-t-2xl">
          <div className="flex justify-center gap-3 mb-4">
            <img src={TAABIY} alt="Taàbiy" className="w-14 h-14 object-contain animate-bounce-gentle" />
            <img src={YEEBIY} alt="Yeèbiy" className="w-14 h-14 object-contain animate-bounce-gentle" style={{ animationDelay: '0.3s' }} />
          </div>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Welcome to L&aacute;mFon
          </h1>
          <p className="text-sm text-white/80 mt-1">Phase 1 Beta</p>
        </div>

        {/* Content */}
        <div className="px-6 -mt-6">
          {/* Mission card */}
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 shadow-sm mb-4">
            {user.level >= 4 && (
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-[#D4A373]" />
                <span className="text-xs font-bold text-[#D4A373]">Wirfon (Elder) Access</span>
              </div>
            )}
            <p className="text-sm text-[#1C1917] leading-relaxed">
              This beta is focused on <strong>strengthening the Lamnso' language foundation</strong>. 
              Your input as {user.level >= 4 ? 'a Wirfon carries special weight in' : 'a community member helps with'} validating 
              translations, spellings, meanings, and usage.
            </p>
            <p className="text-xs text-[#57534E] mt-3 leading-relaxed">
              Some features are still in progress — lessons, audio, and broader community tools are growing. 
              This phase is centered on <strong>trusted validation</strong> by people who know the language best.
            </p>
          </div>

          {/* What you can do */}
          <div className="bg-white rounded-2xl border border-[#E7E5E4] p-5 shadow-sm mb-4">
            <p className="text-xs font-bold text-[#57534E] uppercase tracking-wider mb-3">What you can do</p>
            <div className="space-y-3">
              <ActionItem icon={<Search className="w-4 h-4 text-[#4CAF50]" />} color="bg-[#E8F5E9]"
                title="Search & Explore" desc="Search 13,000+ Lamnso' translations" />
              <ActionItem icon={<CheckSquare className="w-4 h-4 text-[#D4A373]" />} color="bg-[#FDF6F0]"
                title="Validate Translations" desc={user.level >= 4 ? "Confirm, reject, or suggest improvements as Wirfon" : "Vote on translation accuracy"} />
              <ActionItem icon={<BookOpen className="w-4 h-4 text-[#588157]" />} color="bg-[#EAF1EA]"
                title="Review Dictionary Data" desc="Help verify senses, examples, and meaning conflicts" />
              <ActionItem icon={<AlertCircle className="w-4 h-4 text-[#0891B2]" />} color="bg-[#ECFEFF]"
                title="Report Issues" desc="Flag problems so we can fix them quickly" />
            </div>
          </div>

          {/* CTA buttons */}
          <div className="space-y-2.5 mb-4">
            {user.level >= 4 && (
              <Link to="/validate" onClick={onClose} className="block">
                <Button className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl py-3 font-semibold gap-2" data-testid="onboarding-start-reviewing">
                  <CheckSquare className="w-4 h-4" /> Start Reviewing
                </Button>
              </Link>
            )}
            <Link to="/search" onClick={onClose} className="block">
              <Button variant="outline" className="w-full rounded-xl py-3 font-semibold gap-2 border-[#E7E5E4]" data-testid="onboarding-explore">
                <Search className="w-4 h-4" /> Explore Translate
              </Button>
            </Link>
            <button onClick={onClose} className="w-full text-center py-2 text-sm text-[#A8A29E] hover:text-[#57534E]" data-testid="onboarding-continue">
              Continue to App
            </button>
          </div>

          {/* Footer note */}
          <div className="text-center pb-6">
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-[#A8A29E]">
              <Heart className="w-3 h-3 text-[#4CAF50]" />
              <span>Thank you for helping preserve Lamnso'</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionItem({ icon, color, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-8 h-8 rounded-xl ${color} flex items-center justify-center flex-shrink-0 mt-0.5`}>{icon}</div>
      <div>
        <p className="text-sm font-semibold text-[#1C1917]">{title}</p>
        <p className="text-[10px] text-[#57534E] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

/**
 * Compact Beta Info Card — shown in the Home page for users to revisit.
 * Can be removed after Phase 1 Beta concludes.
 */
export function BetaInfoCard({ user }) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div className="bg-gradient-to-r from-[#E8F5E9] to-[#FDF6F0] rounded-2xl p-4 mb-4 relative" data-testid="beta-info-card">
      <button onClick={() => setDismissed(true)} className="absolute top-3 right-3 text-[#A8A29E] hover:text-[#57534E]">
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-[#4CAF50]" />
        <span className="text-xs font-bold text-[#4CAF50]">Phase 1 Beta</span>
      </div>
      <p className="text-xs text-[#57534E] leading-relaxed pr-4">
        You're helping build the foundation for Lamnso' language preservation. 
        {user?.level >= 4 ? ' As a Wirfon, your reviews carry extra weight.' : ' Every contribution matters.'}
      </p>
      <div className="flex gap-2 mt-3">
        {user?.level >= 4 && (
          <Link to="/validate">
            <Button size="sm" className="bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl text-[10px] gap-1 px-3" data-testid="beta-card-review">
              <CheckSquare className="w-3 h-3" /> Review Translations
            </Button>
          </Link>
        )}
        <Link to="/search">
          <Button size="sm" variant="outline" className="rounded-xl text-[10px] gap-1 px-3" data-testid="beta-card-search">
            <Search className="w-3 h-3" /> Search
          </Button>
        </Link>
      </div>
    </div>
  );
}

/**
 * Check if onboarding should be shown.
 */
export function shouldShowOnboarding(userId) {
  const seen = localStorage.getItem(STORAGE_KEY);
  if (!seen) return true;
  try {
    const data = JSON.parse(seen);
    return !data[userId];
  } catch {
    return true;
  }
}

/**
 * Mark onboarding as seen for a user.
 */
export function markOnboardingSeen(userId) {
  let data = {};
  try {
    data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (err) { console.error(err) }
  data[userId] = Date.now();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}
